import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduStudentWorkerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { GraduationCap, Plus, Pencil, Trash2, Eye, Loader2, Users, Clock, BookOpen } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const WORKER_TYPES = ['TA', 'RA', 'intern', 'ambassador', 'worker'];
const TYPE_LABEL = {
  TA: 'Teaching Assistant', RA: 'Research Assistant', intern: 'Intern',
  ambassador: 'Ambassador', worker: 'Worker',
};

const EMPTY_FORM = {
  person_ref: '', name: '', worker_type: 'TA', program: '',
  supervisor_employee_id: '', max_weekly_hours: 0, is_active: true,
};

const EMPTY_ASSIGNMENT = { course_or_project: '', hours: 0, hourly_rate: 0, term_id: '' };

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationStudentWorkerManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [workers, setWorkers] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [detail, setDetail] = useState(null);
  const [assignForm, setAssignForm] = useState(EMPTY_ASSIGNMENT);

  const loadWorkers = async () => {
    setLoading(true);
    try {
      const res = await eduStudentWorkerAPI.list();
      setWorkers(toArray(res));
    } catch (err) {
      // 402 → add-on not active; surface a clear, non-alarming message.
      if (err?.response?.status === 402) {
        setWorkers([]);
      } else {
        Swal.fire('Could not load student workers', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadWorkers(); }, []);

  const stats = useMemo(() => {
    const total = workers.length;
    const byType = WORKER_TYPES.reduce((acc, t) => {
      acc[t] = workers.filter((w) => w.worker_type === t).length;
      return acc;
    }, {});
    const hours = workers.reduce((s, w) => s + (Number(w.max_weekly_hours) || 0), 0);
    return { total, ta: byType.TA, ra: byType.RA, hours };
  }, [workers]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...EMPTY_FORM, ...row,
      supervisor_employee_id: row.supervisor_employee_id != null ? String(row.supervisor_employee_id) : '',
    });
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.name?.trim()) return Swal.fire('Name required', 'Enter the student worker name.', 'warning');
    const payload = {
      ...form,
      max_weekly_hours: Number(form.max_weekly_hours) || 0,
      supervisor_employee_id: form.supervisor_employee_id ? Number(form.supervisor_employee_id) : null,
    };
    try {
      if (editingId) {
        await eduStudentWorkerAPI.update(editingId, payload);
      } else {
        await eduStudentWorkerAPI.create(payload);
      }
      setDialogOpen(false);
      await loadWorkers();
      Swal.fire({ icon: 'success', title: editingId ? 'Worker updated' : 'Worker added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const openDetail = async (row) => {
    try {
      const res = await eduStudentWorkerAPI.get(row.id);
      setDetail(res.data);
      setAssignForm(EMPTY_ASSIGNMENT);
    } catch (err) {
      Swal.fire('Could not open profile', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const setAssignField = (key, value) => setAssignForm((prev) => ({ ...prev, [key]: value }));

  const addAssignment = async () => {
    if (!detail) return;
    if (!assignForm.course_or_project?.trim()) {
      return Swal.fire('Course / project required', 'Enter a course or project name.', 'warning');
    }
    try {
      const res = await eduStudentWorkerAPI.addAssignment(detail.id, {
        course_or_project: assignForm.course_or_project,
        hours: Number(assignForm.hours) || 0,
        hourly_rate: Number(assignForm.hourly_rate) || 0,
        term_id: assignForm.term_id ? Number(assignForm.term_id) : null,
      });
      setDetail(res.data);
      setAssignForm(EMPTY_ASSIGNMENT);
      await loadWorkers();
    } catch (err) {
      Swal.fire('Could not add assignment', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      const res = await eduStudentWorkerAPI.removeAssignment(assignmentId);
      setDetail(res.data);
      await loadWorkers();
    } catch (err) {
      Swal.fire('Could not remove assignment', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete student worker?',
      text: `${row.name} and their assignments will be removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduStudentWorkerAPI.remove(row.id);
      await loadWorkers();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-600" /> Student Worker Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Teaching and research assistants, interns, ambassadors and casual workers — with per-course/project assignments.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> Add Student Worker
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Workers" value={stats.total} icon={Users} />
        <StatCard label="Teaching Assistants" value={stats.ta} icon={BookOpen} />
        <StatCard label="Research Assistants" value={stats.ra} icon={GraduationCap} />
        <StatCard label="Max Weekly Hours" value={`${stats.hours} hrs`} icon={Clock} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Student Workers</CardTitle>
          <CardDescription>All student workers engaged by this institute.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading student workers…
            </div>
          ) : workers.length === 0 ? (
            <EmptyState
              icon={GraduationCap}
              title="No student workers yet"
              description={canManage ? 'Add a student worker to get started.' : 'No student workers have been added yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Roll / ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Max Hrs/Wk</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workers.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.person_ref || '—'}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell><Badge variant="secondary">{TYPE_LABEL[row.worker_type] || row.worker_type}</Badge></TableCell>
                      <TableCell>{row.program || '—'}</TableCell>
                      <TableCell>{row.max_weekly_hours ? `${row.max_weekly_hours} hrs` : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={row.is_active ? 'secondary' : 'outline'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => openDetail(row)} title="View"><Eye className="h-4 w-4" /></Button>
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Student Worker' : 'Add Student Worker'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Full name" />
              </div>
              <div className="space-y-1">
                <Label>Roll / Student ID</Label>
                <Input value={form.person_ref || ''} onChange={(e) => setField('person_ref', e.target.value)} placeholder="2021CS042" />
              </div>
              <div className="space-y-1">
                <Label>Worker Type</Label>
                <Select value={form.worker_type} onValueChange={(v) => setField('worker_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WORKER_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Program</Label>
                <Input value={form.program || ''} onChange={(e) => setField('program', e.target.value)} placeholder="B.Tech CSE" />
              </div>
              <div className="space-y-1">
                <Label>Supervisor Employee ID</Label>
                <Input type="number" value={form.supervisor_employee_id || ''} onChange={(e) => setField('supervisor_employee_id', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Max Weekly Hours</Label>
                <Input type="number" value={form.max_weekly_hours} onChange={(e) => setField('max_weekly_hours', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={form.is_active ? 'active' : 'inactive'} onValueChange={(v) => setField('is_active', v === 'active')}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </DialogFormGrid>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Add worker'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detail.name}
                  <Badge variant="secondary">{TYPE_LABEL[detail.worker_type] || detail.worker_type}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm text-muted-foreground">
                {detail.person_ref || '—'}{detail.program ? ` · ${detail.program}` : ''} · Max {detail.max_weekly_hours || 0} hrs/wk
              </div>

              <div className="mt-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Assignments</div>
                {(detail.assignments || []).length === 0
                  ? <div className="text-sm text-muted-foreground mb-2">None recorded.</div>
                  : (
                    <div className="overflow-x-auto mb-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Course / Project</TableHead>
                            <TableHead>Hours</TableHead>
                            <TableHead>Rate</TableHead>
                            <TableHead>Term</TableHead>
                            {canManage && <TableHead className="text-right">Actions</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detail.assignments.map((a) => (
                            <TableRow key={a.id}>
                              <TableCell className="font-medium">{a.course_or_project || '—'}</TableCell>
                              <TableCell>{a.hours || 0}</TableCell>
                              <TableCell>{a.hourly_rate || 0}</TableCell>
                              <TableCell>{a.term_id ?? '—'}</TableCell>
                              {canManage && (
                                <TableCell className="text-right">
                                  <Button variant="ghost" size="icon" onClick={() => removeAssignment(a.id)} title="Remove">
                                    <Trash2 className="h-4 w-4 text-rose-600" />
                                  </Button>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                {canManage && (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end border-t pt-3">
                    <div className="space-y-1 md:col-span-2">
                      <Label>Course / Project</Label>
                      <Input value={assignForm.course_or_project} onChange={(e) => setAssignField('course_or_project', e.target.value)} placeholder="CS101 Lab" />
                    </div>
                    <div className="space-y-1">
                      <Label>Hours</Label>
                      <Input type="number" value={assignForm.hours} onChange={(e) => setAssignField('hours', e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label>Rate</Label>
                      <Input type="number" value={assignForm.hourly_rate} onChange={(e) => setAssignField('hourly_rate', e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Button onClick={addAssignment} className="w-full"><Plus className="h-4 w-4 mr-1" /> Add</Button>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationStudentWorkerManagement;
